/**
 * Translates raw Entra External ID (AADSTS) error descriptions
 * into user-friendly Portuguese messages.
 */

const AADSTS_MAP: Record<string, string> = {
  // User/account issues
  '50034': 'Conta não encontrada. Verifique seu CPF ou crie uma nova conta.',
  '50020': 'Esta conta não está autorizada a acessar o sistema.',
  '50022': 'Esta conta não está autorizada a acessar o sistema.',

  // Credential issues
  '50126': 'CPF ou senha inválidos. Verifique suas credenciais e tente novamente.',

  // Password issues
  '50055': 'Sua senha expirou. Por favor, redefina sua senha para continuar.',
  '50056': 'A senha desta conta está desabilitada.',

  // Password policy violations (signup / reset)
  '399249': 'Esta senha é muito comum ou fraca. Escolha uma senha mais forte e diferente.',
  '50054': 'Você já usou essa senha recentemente. Escolha uma senha inédita.',

  // Account state
  '50053': 'Sua conta está temporariamente bloqueada por excesso de tentativas. Tente novamente mais tarde.',
  '50057': 'Esta conta está desabilitada. Entre em contato com o suporte.',
  '50059': 'Conta não encontrada no sistema. Verifique seus dados ou crie uma nova conta.',
  '50074': 'Autenticação multifator é necessária para esta conta.',

  // Token/session issues
  '50173': 'Sua sessão expirou. Por favor, faça login novamente.',
  '50076': 'Autenticação multifator é necessária para esta conta.',
  '50131': 'Acesso bloqueado por política de segurança. Verifique suas credenciais.',

  // Conditional access
  '53003': 'Acesso bloqueado pela política de segurança do sistema.',
  '530011': 'Acesso bloqueado. Seu local de acesso não é permitido.',
  '530012': 'Acesso bloqueado. Seu navegador não é permitido.',

  // Application errors
  '700016': 'Erro de configuração do sistema. Tente novamente mais tarde.',
  '7000215': 'Erro de configuração do sistema. Tente novamente mais tarde.',
  '700027': 'Erro de configuração do sistema. Tente novamente mais tarde.',
  '7000218': 'Erro de configuração do sistema. Tente novamente mais tarde.',
};

const AADSTS_PATTERN = /AADSTS(\d{5,6})/;

const DEFAULT_MESSAGE = 'CPF ou senha inválidos. Verifique suas credenciais e tente novamente.';

/**
 * Translates a raw Entra error description (which may contain an AADSTS code)
 * into a user-friendly Portuguese message.
 *
 * @param errorDescription - Raw error from MSAL or Entra API response
 * @returns A friendly Portuguese error message
 */
export function translateEntraError(errorDescription: string | undefined | null): string {
  if (!errorDescription) return DEFAULT_MESSAGE;

  const match = errorDescription.match(AADSTS_PATTERN);
  if (match && match[1]) {
    const code = match[1];
    return AADSTS_MAP[code] ?? DEFAULT_MESSAGE;
  }

  // Fallback: if the message doesn't contain an AADSTS code, return a generic message
  // rather than exposing the raw technical text
  return DEFAULT_MESSAGE;
}

const PASSWORD_POLICY_CODES = new Set(['399249', '50054']);

const PASSWORD_POLICY_PATTERNS = [
  'complexity',
  'requirements',
  'password_too_weak',
  'password_banned',
  'password_is_invalid',
  'password_too_short',
  'password_too_long',
  'password_recently_used',
];

export function isPasswordPolicyError(errorDescription: string | undefined | null): boolean {
  if (!errorDescription) return false;

  const match = errorDescription.match(AADSTS_PATTERN);
  if (match && match[1] && PASSWORD_POLICY_CODES.has(match[1])) {
    return true;
  }

  const lower = errorDescription.toLowerCase();
  return PASSWORD_POLICY_PATTERNS.some((p) => lower.includes(p));
}
